import { createBlocksClient } from "@seliseblocks/client";
import { BLOCKS_API_URL, PROJECT_KEY } from "../../blocks/config";
import apiClient from "../axios";
import gqlClient from "../graphql.client";
import { GET_POST_CARDS_QUERY } from "./posts.query";
import type { Post } from "@/src/models/post.model";
import type { FeedSort } from "@/src/utils/feed-sort";

const feedId = process.env.EXPO_PUBLIC_FEED_FUNCTION_ID ?? "";
const boostId = process.env.EXPO_PUBLIC_FEED_BOOST_FUNCTION_ID ?? "";
export const feedRankingEnabled = !!feedId;
export const feedBoostEnabled = !!feedId && !!boostId;
// Keep the app's cookie refresh behavior while using the Blocks SDK transport.
const makeClient = () => createBlocksClient({apiUrl: BLOCKS_API_URL, xBlocksKey: PROJECT_KEY,
  fetch: async (url, init) => {
    const response = await apiClient.request({url: String(url), method: init?.method,
      data: init?.body, headers: Object.fromEntries(new Headers(init?.headers).entries()),
      timeout: 30000, validateStatus: status => status !== 401});
    return new Response(JSON.stringify(response.data), {status: response.status,
      headers: {"content-type": "application/json"}});
  },
});
const POLL_DEADLINE_MS = 30000;
class FeedFunctionError extends Error {
  constructor(public code: string, public status: number) {super(code);}
}
export const isFeedCursorExpired = (error: unknown) => error instanceof FeedFunctionError && error.code === "FEED_CURSOR_EXPIRED";
async function invoke<T>(id: string, body: Record<string, unknown>): Promise<T> {
  if (!id) throw new Error("Feed function not configured");
  const blocks = makeClient();
  let value: any;
  try {value = await blocks.http.request(`/logic/v4/fn/${encodeURIComponent(id)}`, {
    method: "POST", body, headers: {Prefer: "wait=25"}});
  } catch (error: any) {
    if (error.status === 409 && error.body?.code === "FEED_CURSOR_EXPIRED") throw new FeedFunctionError("FEED_CURSOR_EXPIRED", 409);
    throw error;
  }
  if (value?.runId && value?.pollToken) {
    const {runId, pollToken} = value;
    // Back off and give up after a deadline. A flat 500ms × 90 poll meant a run
    // stuck in QUEUED produced ~90 requests per call, repeated by every retry
    // and every head refresh.
    const deadline = Date.now() + POLL_DEADLINE_MS;
    for (let delay = 500; Date.now() < deadline; delay = Math.min(delay * 1.5, 4000)) {
      await new Promise(resolve => setTimeout(resolve, delay));
      const result: any = await blocks.http.request(`/logic/v4/fn/runs/${encodeURIComponent(runId)}/result`, {
        headers: {"x-poll-token": pollToken}});
      if (String(result.status).toUpperCase() === "SUCCEEDED") {
        value = typeof result.result === "string" ? JSON.parse(result.result) : result.result;
        if (typeof value?.statusCode === "number") {
          if (value.statusCode >= 400) throw new FeedFunctionError(value.body?.code ?? "FEED_UNAVAILABLE", value.statusCode);
          value = value.body;
        }
        break;
      }
      if (["FAILED", "TIMEDOUT", "TIMED_OUT", "CANCELLED", "CANCELED"].includes(String(result.status).toUpperCase())) throw new Error("Feed request failed");
    }
  }
  if (!value || value.runId) throw new Error("Feed request timed out");
  return value as T;
}
export async function rankedPage(sort: FeedSort, groupId: string | undefined, cursor?: string, pageSize = 5) {
  const result = await invoke<{items: {id: string; boostedUntil: string | null}[]; nextCursor: string | null}>(feedId,
    {sort, groupId: groupId ?? "", pageSize, ...(cursor ? {cursor} : {})});
  if (cursor && result.nextCursor === cursor) throw new FeedFunctionError("FEED_CURSOR_EXPIRED", 409);
  const client = await gqlClient();
  const posts = result.items.length ? await client.request<{getposts: {items: Post[]}}>(GET_POST_CARDS_QUERY, {
    where: {ItemId: {in: result.items.map(p => p.id)}}, order: [], pageNo: 1, pageSize: result.items.length,
  }) : {getposts: {items: []}};
  const byId = new Map(posts.getposts.items.map(p => [p.ItemId, p]));
  return {getposts: {items: result.items.flatMap(p => {
    const post = byId.get(p.id); return post ? [{...post, feedBoostedUntil: p.boostedUntil}] : [];
  }), hasNextPage: !!result.nextCursor, nextCursor: result.nextCursor, pageNo: 1}};
}
/** Ids of the first ranked page only: the cheap "is there anything new?" check. */
export async function rankedHeadIds(sort: FeedSort, groupId: string | undefined): Promise<string[]> {
  const result = await invoke<{items: {id: string}[]}>(feedId, {sort, groupId: groupId ?? "", pageSize: 5});
  return result.items.map(p => p.id);
}
export async function boostPost(postId: string, hours: 24 | 48, remove = false) {
  const requestId = Array.from(crypto.getRandomValues(new Uint8Array(16)), n => n.toString(16).padStart(2, "0")).join("");
  const body = {postId, hours, action: remove ? "remove" : "boost", requestId};
  try {await invoke(boostId, body);} catch (error: any) {
    if (error.status || error instanceof FeedFunctionError) throw error;
    await invoke(boostId, body);
  }
}
